// The cases the Investigate tab lists, built from the placed findings and
// what Investigate has written for the scope. A finding no organize pass has
// reached yet stands in a case of its own act -- the findings of one session
// that quote the same evidence, which are never told apart -- until a pass
// folds it into a story. Every case sits under the group of its most pressing
// finding; its other findings keep their own groups inside it.
import { GROUP_ORDER, SET_ASIDE, comparePlaced, groupRank } from "./investigateFacts.js";
import { severityRank } from "./triageLite.js";
import { parseServerTimestamp } from "./traceUtils.js";

const str = (v) => (v == null ? "" : String(v));
const lower = (v) => str(v).toLowerCase();
export const PROVISIONAL = "p:";
export const isProvisional = (id) => str(id).startsWith(PROVISIONAL);

const NEEDS_ORDER = { yes: 0, unsure: 1, null: 2, no: 3 };
const needsRank = (c) => NEEDS_ORDER[c.dig?.needsPerson ?? "null"] ?? 2;

// One case as the tab draws it (and the case inbox, lib/caseInbox.js).
export function caseView(base, placed) {
    const items = [...placed].sort(comparePlaced);
    const lead = items[0];
    const sessions = [...new Set(items.map((p) => lower(p.finding.session)).filter(Boolean))];
    const endpoints = [...new Set(items.map((p) => lower(p.finding.endpoint)).filter(Boolean))];
    const times = items.map((p) => parseServerTimestamp(p.finding.completedAt)).filter(Number.isFinite);
    const groups = [...new Set(items.map((p) => p.group))].sort((a, b) => groupRank(a) - groupRank(b));
    const metBy = [...new Set(items.flatMap((p) => (p.group === "needs_you" ? p.verdict?.metBy || [] : [])))];
    const notes = base.notes || [];
    return {
        id: base.id,
        provisional: isProvisional(base.id),
        title: base.title || lead?.finding.title || "Untitled",
        account: base.account || "",
        by: base.by || "investigate",
        group: lead?.group || "unchecked",
        groups,
        items,
        findingIds: items.map((p) => p.finding.id),
        sessions,
        endpoints,
        severity: lead ? items.reduce((worst, p) => (severityRank(p.finding.severity) > severityRank(worst) ? p.finding.severity : worst), lead.finding.severity) : "",
        latestAt: times.length ? Math.max(...times) : NaN,
        firstAt: times.length ? Math.min(...times) : NaN,
        metBy,
        matches: base.matches || [],
        dig: base.dig || null,
        notes,
        related: base.related || [],
        corrected: items.filter((p) => p.source === "corrected").length,
        checkedHere: items.filter((p) => p.source === "here").length,
        missed: notes.filter((n) => n.kind === "missed" || n.kind === "corrects").length,
    };
}

// Inside a group: what a dig found needs a person first, then what matches
// the reader's priorities, then the loudest, the widest and the newest.
export function compareCases(a, b) {
    return (groupRank(a.group) - groupRank(b.group))
        || (needsRank(a) - needsRank(b))
        || (b.matches.length - a.matches.length)
        || (severityRank(b.severity) - severityRank(a.severity))
        || (b.sessions.length - a.sessions.length)
        || ((Number.isFinite(b.latestAt) ? b.latestAt : 0) - (Number.isFinite(a.latestAt) ? a.latestAt : 0))
        || a.id.localeCompare(b.id);
}

// `placed`: the findings in view, each placed (investigateFacts
// placeFindings). `map`: the pool's map, for each finding's incident.
// `investigation`: the scope's stored cases.
// Returns { cases, byId, caseOf, groups: [{ id, cases, findings, elsewhere,
// moved, reasons, setAside }], pending }
// where `pending` counts the findings in provisional cases, by group.
export function buildCases({ placed = [], map = null, investigation = null } = {}) {
    const byFinding = new Map(placed.map((p) => [lower(p.finding.id), p]));
    const caseOf = new Map();
    const views = [];
    for (const c of investigation?.cases || []) {
        const items = c.findings.map((id) => byFinding.get(id)).filter(Boolean);
        if (!items.length) continue;
        for (const p of items) caseOf.set(lower(p.finding.id), c.id);
        views.push(caseView(c, items));
    }
    // What no stored case holds stands in a case of its own act.
    const loose = new Map();
    for (const p of placed) {
        const id = lower(p.finding.id);
        if (caseOf.has(id)) continue;
        const incident = map?.findings?.get?.(p.finding.id)?.incident || `i:${id}`;
        const key = `${PROVISIONAL}${incident}`;
        if (!loose.has(key)) loose.set(key, []);
        loose.get(key).push(p);
        caseOf.set(id, key);
    }
    const pending = {};
    for (const [key, items] of loose) {
        const view = caseView({ id: key, notes: [], related: [], matches: [] }, items);
        views.push(view);
        pending[view.group] = (pending[view.group] || 0) + items.length;
    }
    views.sort(compareCases);
    const byId = new Map(views.map((v) => [v.id, v]));
    return { cases: views, byId, caseOf, groups: groupsOf(views), pending };
}

// The groups a list of cases falls in. A group counts the findings placed in
// it, as the flow above the tab does, wherever their case is listed: it lists
// the cases it leads, and its findings inside a case a group above it leads
// are `elsewhere`. `moved`: those placed here by Investigate's own checks and
// corrections rather than by the backend's check.
export function groupsOf(views) {
    return GROUP_ORDER.map((id) => {
        const cases = views.filter((v) => v.group === id);
        const reasons = {};
        let findings = 0;
        let listed = 0;
        let moved = 0;
        for (const v of views) {
            for (const p of v.items) {
                if (p.group !== id) continue;
                findings += 1;
                if (v.group === id) listed += 1;
                if (p.source === "corrected" || p.source === "here") moved += 1;
                if (p.reason) reasons[p.reason] = (reasons[p.reason] || 0) + 1;
            }
        }
        return { id, cases, findings, elsewhere: findings - listed, moved, reasons, setAside: SET_ASIDE.has(id) };
    });
}

// The findings an organize pass should fold, most pressing first: those in
// provisional cases under the groups it covers.
export function unorganized(built, groups) {
    const wanted = new Set(groups);
    return built.cases.filter((c) => c.provisional && wanted.has(c.group));
}
